import {
  BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE,
  BOOKING_SESSION_TYPE,
  BOOKING_STATUS,
  BOOKING_TYPE,
} from 'lib/constants';

// Remote only for now. Build has no location field, so an in-person group redirected in here would
// be asked to pick a video-call tool and never shown where it actually happens; longitudinal brings
// its own scheduling shape. Both widen this line once those are answered.
const SCHEDULED_BOOKING_TYPES: readonly number[] = [BOOKING_TYPE.REMOTE];

type StudyShape = {
  type?: number | null;
  status?: number | null;
  config?: {
    expert_novice_study?: boolean | null;
    session?: { type?: number | null; slot_max?: number | null } | null;
    participant_agreement?: { type?: number | null } | null;
  } | null;
};

/**
 * Whether this booking belongs in the focus-group experience. Deliberately independent of the Labs
 * flag: the flag controls who may reach the routes, the booking's own shape decides what it is.
 */
export const isFocusGroupStudy = ({ study }: { study: StudyShape | null | undefined }): boolean =>
  !!study &&
  SCHEDULED_BOOKING_TYPES.includes(study.type ?? -1) &&
  study.config?.session?.type === BOOKING_SESSION_TYPE.GROUP;

/**
 * Expert/novice is an explicit study configuration. A Labs user opening an ordinary focus group must
 * not inherit pair constraints, so this reads the marker and nothing else.
 */
export const isExpertNoviceStudy = ({ study }: { study: StudyShape | null | undefined }): boolean =>
  study?.config?.expert_novice_study === true;

export const isStudyCompleted = ({ study }: { study: StudyShape | null | undefined }): boolean =>
  study?.status === BOOKING_STATUS.COMPLETED;

type TeamShape = {
  team?: {
    users?: ReadonlyArray<{
      status?: unknown;
      User?: { _id?: string | null; displayName?: string | null; email?: string | null } | null;
    } | null> | null;
  } | null;
};

/** Team members who can run a session. A missing status reads as active, as the server treats it. */
export const teamFacilitators = ({ study }: { study: TeamShape }): { id: string; name: string | null }[] =>
  (study.team?.users ?? []).flatMap(member =>
    member?.User?._id && member.status !== 0
      ? [{ id: member.User._id, name: member.User.displayName || member.User.email || null }]
      : [],
  );

/** Expert/novice sessions are always one expert plus one novice, whatever `slot_max` happens to say. */
export const focusGroupSize = ({ study }: { study: StudyShape | null | undefined }): number =>
  isExpertNoviceStudy({ study }) ? 2 : Math.max(2, study?.config?.session?.slot_max ?? 2);

export const isStudyLive = ({ study }: { study: StudyShape | null | undefined }): boolean =>
  study?.status !== BOOKING_STATUS.DRAFT;

/**
 * Whether participants have an agreement to sign. Only a custom agreement produces a per-submission
 * record to report — Askable's own terms are accepted on the platform, not per study, so there is
 * nothing outstanding to chase.
 */
export const requiresParticipantAgreement = ({ study }: { study: StudyShape | null | undefined }): boolean =>
  study?.config?.participant_agreement?.type === BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE.CUSTOM_AGREEMENT;
